<template>
    <page-loading v-if="loading"></page-loading>
    <form-container v-else>
        <step-form-heading v-if="!props.groupId" :icon="faGrid2Plus">
            New Group

            <template v-slot:description>
                Create a group to connect specific users to a set of resources in Voxie.
            </template>
        </step-form-heading>

        <page-header v-else class="mb-6 lg:mb-12">
            <template v-if="onCorporate()" v-slot:subtitle>
                <a :href="`/settings/teams/${teamId}#/groups`"
                   class="no-underline! text-sky-600! text-base font-normal">
                    <font-awesome-icon widthAuto class="mr-1" :icon="faArrowLeft"></font-awesome-icon>
                    All Groups
                </a>
            </template>

            {{ onCorporate() ? group.name : 'Details'}}
            <group-status-badge v-if="group.status" :status="group.status" class="ms-3" />
        </page-header>

        <div :class="{
            'grid grid-cols-12': onCorporate() && props.groupId,
            'max-w-(--breakpoint-sm)': !onCorporate()
        }">
            <page-sidebar v-if="onCorporate() && props.groupId" appearance="nested" :links="sidebarLinks"></page-sidebar>

            <div class="col-span-12 lg:col-span-8">
                <vx-alert v-if="group.org_unit && (onCorporate() || (roleOnCurrentOrgUnit === 'admin' && currentOrgUnit?.id === group.org_unit.id))" :key="group" class="col-span-full mb-6" :closeable="false" color="info" shadow :class="{
                    'max-w-(--breakpoint-sm) mx-auto': !props.groupId
                }">
                    <template v-slot:icon>
                        <font-awesome-icon widthAuto :icon="faSlidersSimple" />
                    </template>

                    Part of <strong>{{group.org_unit?.name}}</strong>

                    <template v-slot:right>
                        <vx-button size="sm" color="info" :href="onCorporate() ? `/settings/teams/${group.team_id}/org-units/${group.org_unit.id}/groups` : `/settings/teams/${group.team_id}#/current-org-unit-groups`">
                            Back to OU
                            <font-awesome-icon widthAuto :icon="faSquareArrowUpLeft"></font-awesome-icon>
                        </vx-button>
                    </template>
                </vx-alert>

                <slot></slot>
            </div>
        </div>
    </form-container>
</template>

<script setup>
import { faArrowLeft, faGrid2Plus, faSlidersSimple, faSquareArrowUpLeft } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { computed, onMounted  } from 'vue';
import StepFormHeading from '~/components/general/StepFormHeading.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import PageHeader from '~/components/general/PageHeader.vue';
import PageSidebar from '~/components/general/PageSidebar.vue';
import { useGroupDetails } from './useGroupDetails';
import FormContainer from '~/components/general/FormContainer.vue';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { VxAlert, VxButton } from '@voxie/frontend-components';
import { FEATURES } from '~/provides/features';
import GroupStatusBadge from '~/components/general/GroupStatusBadge.vue';

const teamId = Spark.state.currentTeam.id;

const props = defineProps({
    groupId: {
        type: String,
        required: false,
    },
});

const { mount, group, loading } = useGroupDetails();
const { onCorporate, roleOnCurrentOrgUnit, currentOrgUnit } = useCurrentGroup();

onMounted(() => {
    mount(props.groupId);
});

const sidebarLinks = computed(() => {
    const links = [
        { href: `/settings/teams/${teamId}/groups/${props.groupId}`, label: 'Details' },
        { href: `/settings/teams/${teamId}/groups/${props.groupId}/users`, label: 'Users' },
        { href: `/settings/teams/${teamId}/groups/${props.groupId}/collections`, label: 'Collections' },
        { label: 'Resources', links: resourcesLinks.value },
    ];

    if (group.value?.is_billable) {
        links.push(
            { href: `/settings/teams/${teamId}/groups/${props.groupId}/billing-details`, label: 'Billing Details' },
            { href: `/settings/teams/${teamId}/groups/${props.groupId}/proforma-invoices`, label: 'Upcoming Invoices' },
            { href: `/settings/teams/${teamId}/groups/${props.groupId}/historical-invoices`, label: 'Historical Invoices' },
        );
    }

    return links;
});

const resourcesLinks = computed(() => {
    const links = [
        {
            label: 'Contacts',
            href: `/settings/teams/${teamId}/groups/${props.groupId}/contacts`,
        },
        {
            label: 'Segments',
            href: `/settings/teams/${teamId}/groups/${props.groupId}/segments`,
        },
        {
            label: 'Campaigns',
            href: `/settings/teams/${teamId}/groups/${props.groupId}/campaigns`,
        },
        {
            label: 'Snippets',
            href: `/settings/teams/${teamId}/groups/${props.groupId}/snippets`,
        },
    ];

    if (FEATURES.calendly_integration) {
        links.push({
            label: 'Calendly',
            href: `/settings/teams/${teamId}/groups/${props.groupId}/calendly`,
        });
    }

    if (FEATURES.acuity_integration) {
        links.push({
            label: 'Acuity',
            href: `/settings/teams/${teamId}/groups/${props.groupId}/acuity`,
        });
    }

    return links;
});

</script>
